import { useCallback, useEffect, useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { AlertTriangle, History, Loader2, Mail, RotateCcw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import {
  EmailControlAuditRow,
  EmailControlKey,
  EmailControlRow,
  EmailSkipNotice,
  FailedEmail,
  acknowledgeSkipNotice,
  emailFailureReason,
  emailStatusLabel,
  loadEmailControlAudit,
  loadEmailControls,
  loadFailedEmails,
  loadSkipNotices,
  resendFailedEmail,
  setEmailControl,
} from "@/lib/emailControls";

const when = (iso?: string | null) => {
  if (!iso) return "never";
  try {
    return `${formatDistanceToNow(new Date(iso))} ago`;
  } catch {
    return iso;
  }
};

/**
 * Settings → Communications. Administrator-only master switches for every
 * automated email, plus the audit trail, skipped-packet notices and failed
 * deliveries that can be resent.
 */
export default function CommunicationsSettingsCard() {
  const [controls, setControls] = useState<EmailControlRow[]>([]);
  const [audit, setAudit] = useState<EmailControlAuditRow[]>([]);
  const [skips, setSkips] = useState<EmailSkipNotice[]>([]);
  const [failed, setFailed] = useState<FailedEmail[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, a, s, f] = await Promise.all([
        loadEmailControls(),
        loadEmailControlAudit(),
        loadSkipNotices(),
        loadFailedEmails().catch(() => [] as FailedEmail[]),
      ]);
      setControls(c);
      setAudit(a);
      setSkips(s);
      setFailed(f);
    } catch (e) {
      toast.error("Couldn't load communication settings", {
        description: (e as Error).message,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const toggle = async (key: EmailControlKey, enabled: boolean) => {
    setBusy(key);
    setControls((prev) => prev.map((c) => (c.control_key === key ? { ...c, enabled } : c)));
    try {
      await setEmailControl(key, enabled);
      toast.success(enabled ? "Turned on" : "Turned off", {
        description: `${controls.find((c) => c.control_key === key)?.label ?? key} — change recorded in the audit log.`,
      });
      const [c, a] = await Promise.all([loadEmailControls(), loadEmailControlAudit()]);
      setControls(c);
      setAudit(a);
    } catch (e) {
      toast.error("Couldn't save that switch", { description: (e as Error).message });
      void load();
    } finally {
      setBusy(null);
    }
  };

  const dismissSkip = async (id: string) => {
    setSkips((prev) => prev.filter((s) => s.id !== id));
    try {
      await acknowledgeSkipNotice(id);
    } catch (e) {
      toast.error("Couldn't dismiss that notice", { description: (e as Error).message });
      void load();
    }
  };

  const resend = async (messageId: string) => {
    setBusy(messageId);
    try {
      await resendFailedEmail(messageId);
      toast.success("Queued", {
        description: "Accepted into the send queue — it will show as Sent once the provider accepts it.",
      });
      setFailed(await loadFailedEmails().catch(() => []));
    } catch (e) {
      toast.error("Resend failed", { description: (e as Error).message });
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Mail className="h-4 w-4" /> Communications
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <p className="text-sm text-muted-foreground">
          Master switches for every automated email. The backend checks these before sending, so a
          switch turned off here stops that email everywhere. A failed email never undoes a
          successful payment or registration.
        </p>

        {loading ? (
          <div className="py-8 flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="space-y-2">
            {controls.map((c) => (
              <div
                key={c.control_key}
                className="rounded-lg border p-3 flex items-start justify-between gap-4"
              >
                <div className="min-w-0 space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium">{c.label}</span>
                    <Badge variant={c.enabled ? "default" : "secondary"}>
                      {c.enabled ? "Sending" : "Paused"}
                    </Badge>
                  </div>
                  {c.description ? (
                    <p className="text-xs text-muted-foreground">{c.description}</p>
                  ) : null}
                  <p className="text-xs text-muted-foreground">
                    Last changed {when(c.updated_at)}
                    {c.updated_by_email ? ` by ${c.updated_by_email}` : ""}
                  </p>
                </div>
                <Switch
                  checked={c.enabled}
                  disabled={busy === c.control_key}
                  onCheckedChange={(v) => toggle(c.control_key, v)}
                  aria-label={c.label}
                />
              </div>
            ))}
          </div>
        )}

        <div className="rounded-lg border border-dashed p-3 text-xs text-muted-foreground flex gap-2">
          <ShieldCheck className="h-4 w-4 shrink-0 mt-0.5" />
          <span>
            Welcome packets have a second gate: even with the switch on, a packet only sends for a
            class or session marked <strong>Ready</strong> in the Class Editor. Publishing a class
            never marks its packet Ready.
          </span>
        </div>

        {skips.length > 0 && (
          <>
            <Separator />
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium">
                <AlertTriangle className="h-4 w-4 text-amber-600" /> Emails intentionally skipped
              </div>
              {skips.map((s) => (
                <div
                  key={s.id}
                  className="rounded-lg border p-3 flex items-start justify-between gap-3"
                >
                  <div className="min-w-0 text-xs space-y-1">
                    <p className="font-medium text-sm truncate">
                      {s.class_name || s.class_slug || s.template_name}
                      {s.session_label ? ` — ${s.session_label}` : ""}
                    </p>
                    <p className="text-muted-foreground">{s.reason}</p>
                    <p className="text-muted-foreground">
                      {s.recipient_email ? `${s.recipient_email} · ` : ""}
                      {when(s.created_at)}
                    </p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => dismissSkip(s.id)}>
                    Dismiss
                  </Button>
                </div>
              ))}
            </div>
          </>
        )}

        {failed.length > 0 && (
          <>
            <Separator />
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium">
                <AlertTriangle className="h-4 w-4 text-destructive" /> Failed deliveries
              </div>
              {failed.map((f) => (
                <div
                  key={f.id}
                  className="rounded-lg border p-3 flex items-start justify-between gap-3"
                >
                  <div className="min-w-0 text-xs space-y-1">
                    <p className="font-medium text-sm truncate">{f.recipient_email}</p>
                    <p className="text-muted-foreground truncate">
                      {f.template_name} · {emailStatusLabel(f.status)} · {when(f.created_at)}
                    </p>
                    <p className="text-muted-foreground break-words">
                      {emailFailureReason(f.error_message)}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy === f.message_id}
                    onClick={() => resend(f.message_id)}
                  >
                    <RotateCcw className="h-3.5 w-3.5 mr-1" /> Resend
                  </Button>
                </div>
              ))}
            </div>
          </>
        )}

        <Separator />
        <Button size="sm" variant="ghost" onClick={() => setShowHistory((v) => !v)}>
          <History className="h-4 w-4 mr-1.5" />
          {showHistory ? "Hide change history" : "Show change history"}
        </Button>
        {showHistory && (
          <div className="space-y-1.5">
            {audit.length === 0 ? (
              <p className="text-xs text-muted-foreground">No changes recorded yet.</p>
            ) : (
              audit.map((a) => (
                <p key={a.id} className="text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">{a.control_key}</span>{" "}
                  {a.previous_enabled === null
                    ? "created"
                    : `${a.previous_enabled ? "on" : "off"} → ${a.new_enabled ? "on" : "off"}`}{" "}
                  · {when(a.created_at)}
                  {a.changed_by_email ? ` · ${a.changed_by_email}` : ""}
                </p>
              ))
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
